import { extractHashtags, inferSemanticTopics } from './topicExtraction';
import { supabase } from '@/integrations/supabase/client';
import { generateRandomGradient } from './gradientGenerator';
import { getProhibitedWords, getSoftBlockWords } from './content-moderation';

// FOMO official communities mapping
const FOMO_COMMUNITIES = {
  art: ['art', 'artist', 'artwork', 'painting', 'drawing', 'illustration', 'sketch', 'canvas', 'artistic', 'creative'],
  photography: ['photo', 'photography', 'photographer', 'camera', 'lens', 'shot', 'capture', 'portrait', 'landscape', 'exposure'],
  '3d': ['3d', 'modeling', 'render', 'blender', 'maya', 'zbrush', 'sculpt', 'mesh', 'texture', 'animation'],
  anime: ['anime', 'manga', 'otaku', 'kawaii', 'senpai', 'waifu', 'shounen', 'shoujo', 'japanese', 'cosplay'],
  cinema: ['movie', 'film', 'cinema', 'director', 'actor', 'actress', 'screenplay', 'cinematography', 'theater', 'hollywood'],
  gaming: ['game', 'gaming', 'gamer', 'playstation', 'xbox', 'nintendo', 'steam', 'esports', 'rpg', 'fps', 'mmorpg']
};

// Vision API labels to community mapping
const VISION_LABEL_MAPPING: Record<string, string[]> = {
  art: ['painting', 'drawing', 'illustration', 'artwork', 'sketch', 'art supplies', 'canvas', 'easel'],
  photography: ['camera', 'lens', 'photography equipment', 'portrait', 'landscape', 'photo'],
  '3d': ['3d render', '3d model', 'computer graphics', 'digital art', 'animation'],
  anime: ['anime', 'manga', 'japanese animation', 'cartoon', 'anime character'],
  cinema: ['movie poster', 'film', 'cinema', 'movie scene', 'film equipment'],
  gaming: ['video game', 'gaming console', 'controller', 'gaming setup', 'esports']
};

export interface ExtractedCommunity {
  communityId?: string;
  communityName: string;
  confidence: number;
  source: 'hashtag' | 'semantic' | 'vision' | 'manual';
  isOfficial: boolean;
}

export interface CommunityExtractionResult {
  communities: ExtractedCommunity[];
  topics: string[];
  visionLabels?: string[];
}

// Comprehensive list of words that should NOT become communities
const STOPWORDS = new Set([
  // Basic stopwords
  'the','a','an','and','or','but','if','then','else','when','while','with','without','within','into','onto','from','to','of','in','on','at','by','for','about','as','like','just','very','really','so','such','that','this','these','those','is','are','was','were','be','been','being','do','does','did','doing','have','has','had','having','it','its','itself','i','me','my','myself','we','our','ours','ourselves','you','your','yours','yourself','yourselves','he','him','his','himself','she','her','hers','herself','they','them','their','theirs','themselves','there','here','over','under','again','once','because','until','during','before','after','up','down','out','off','over','under','too','can','will','just','don','should','now','taken',
  
  // Pronouns and determiners  
  'friend','friends','people','person','someone','everyone','nobody','anybody','somebody','nothing','something','anything','everything','one','ones','other','others','another','each','every','all','some','any','many','much','few','several','most','more','less',
  
  // Generic descriptors and emotions
  'nice','good','bad','cool','awesome','great','amazing','wonderful','terrible','horrible','beautiful','ugly','hot','cold','big','small','huge','tiny','fast','slow','easy','hard','difficult','simple','complex','new','old','young','fresh','stale','clean','dirty','right','wrong','true','false','real','fake','perfect','awful','excellent','poor','rich','cheap','expensive',
  
  // Time and location words
  'today','yesterday','tomorrow','tonight','morning','afternoon','evening','night','day','week','month','year','time','moment','second','minute','hour','soon','later','early','late','always','never','sometimes','often','rarely','usually','here','there','everywhere','nowhere','somewhere','anywhere','where','home','place','location','area','space','room','house','building',
  
  // Platform and social media terms
  'post','posts','share','shares','like','likes','comment','comments','follow','follows','user','users','profile','account','feed','timeline','social','media','platform','app','website','site','online','internet','digital','virtual','content','video','image','photo','picture','pic','pics','upload','download','click','tap','swipe',
  
  // Common actions and states
  'make','makes','making','get','gets','getting','take','takes','taking','give','gives','giving','see','sees','seeing','look','looks','looking','want','wants','wanting','need','needs','needing','try','tries','trying','work','works','working','play','plays','playing','buy','buys','buying','sell','sells','selling','find','finds','finding','know','knows','knowing','think','thinks','thinking','feel','feels','feeling','love','loves','loving','hate','hates','hating','help','helps','helping','talk','talks','talking','tell','tells','telling','say','says','saying','ask','asks','asking','answer','answers','answering','read','reads','reading','write','writes','writing','learn','learns','learning','teach','teaches','teaching','study','studies','studying','remember','remembers','remembering','forget','forgets','forgetting','believe','believes','believing','hope','hopes','hoping','wish','wishes','wishing','dream','dreams','dreaming','sleep','sleeps','sleeping','wake','wakes','waking','eat','eats','eating','drink','drinks','drinking','run','runs','running','walk','walks','walking','sit','sits','sitting','stand','stands','standing','lie','lies','lying','come','comes','coming','go','goes','going','stay','stays','staying','stop','stops','stopping','start','starts','starting','end','ends','ending','begin','begins','beginning','finish','finishes','finishing','continue','continues','continuing','change','changes','changing','move','moves','moving','live','lives','living','die','dies','dying','born','grow','grows','growing','become','becomes','becoming','seem','seems','seeming','appear','appears','appearing','happen','happens','happening','occur','occurs','occurring','exist','exists','existing','matter','matters','mattering','mean','means','meaning','show','shows','showing','tell','tells','telling','explain','explains','explaining','describe','describes','describing','express','expresses','expressing','create','creates','creating','build','builds','building','destroy','destroys','destroying','break','breaks','breaking','fix','fixes','fixing','solve','solves','solving','choose','chooses','choosing','decide','decides','deciding','plan','plans','planning','prepare','prepares','preparing','organize','organizes','organizing','manage','manages','managing','control','controls','controlling','lead','leads','leading','follow','follows','following','join','joins','joining','leave','leaves','leaving','enter','enters','entering','exit','exits','exiting','open','opens','opening','close','closes','closing','lock','locks','locking','unlock','unlocks','unlocking','push','pushes','pushing','pull','pulls','pulling','carry','carries','carrying','hold','holds','holding','grab','grabs','grabbing','throw','throws','throwing','catch','catches','catching','hit','hits','hitting','kick','kicks','kicking','jump','jumps','jumping','climb','climbs','climbing','fall','falls','falling','rise','rises','rising','lift','lifts','lifting','drop','drops','dropping','pick','picks','picking','choose','chooses','choosing','select','selects','selecting',
  
  // Vague quantifiers and modifiers
  'pretty','quite','rather','somewhat','fairly','slightly','extremely','incredibly','absolutely','totally','completely','entirely','partially','mostly','mainly','generally','specifically','particularly','especially','exactly','approximately','roughly','about','around','nearly','almost','barely','hardly','scarcely','definitely','probably','possibly','maybe','perhaps',
  
  // Common interjections and filler words
  'yeah','yes','yep','nope','nah','okay','ok','alright','sure','fine','whatever','anyway','actually','basically','literally','obviously','clearly','honestly','seriously','really','truly','indeed','certainly','definitely','absolutely','exactly','precisely','probably','possibly','maybe','perhaps','well','so','like','you','know','um','uh','oh','ah','wow','cool','nice','great','awesome','amazing','perfect','excellent','wonderful','fantastic','incredible','unbelievable','outstanding','brilliant','superb','magnificent','marvelous','fabulous','terrific','phenomenal','spectacular','remarkable','extraordinary','exceptional','impressive','stunning','breathtaking','gorgeous','beautiful','lovely','adorable','cute','sweet','charming','delightful','pleasant','enjoyable','fun','exciting','thrilling','amazing','incredible','unbelievable','shocking','surprising','astonishing','astounding','mind-blowing','jaw-dropping'
]);

function isTopicalCandidate(term: string, prohibitedWordsSet?: Set<string>): boolean {
  const t = term.toLowerCase().trim();
  if (!t) return false;
  if (STOPWORDS.has(t)) return false;
  
  // CRITICAL: Check against prohibited/soft-blocked words to prevent banned communities
  if (prohibitedWordsSet && prohibitedWordsSet.has(t)) {
    console.warn(`[Community Filter] Rejected banned word as community: "${t}"`);
    return false;
  }
  
  // Must contain letters and be at least 3 chars
  if (!/[a-z]/i.test(t)) return false;
  if (t.length < 3) return false;
  return true;
}

/**
 * Extract communities from text content using hashtags and semantic analysis
 */
export async function extractCommunitiesFromText(
  text: string,
  existingCommunities?: Map<string, { id: string; isOfficial: boolean }>
): Promise<ExtractedCommunity[]> {
  const communities: ExtractedCommunity[] = [];
  const processedNames = new Set<string>();

  // CRITICAL: Load prohibited words to prevent banned communities
  const [prohibitedWords, softBlockWords] = await Promise.all([
    getProhibitedWords(),
    getSoftBlockWords()
  ]);
  
  // Create a Set for fast lookup of banned words
  const bannedWordsSet = new Set<string>();
  prohibitedWords.forEach(w => bannedWordsSet.add(w.word.toLowerCase()));
  softBlockWords.forEach(w => bannedWordsSet.add(w.word.toLowerCase()));
  
  console.log(`[Community Filter] Loaded ${bannedWordsSet.size} banned words for filtering`);

  // Strip @mentions from text before processing to avoid treating usernames as communities
  const textWithoutMentions = text.replace(/@[a-zA-Z0-9_-]+/g, '');

  // 1. Extract hashtag-based communities (highest confidence)
  const hashtags = extractHashtags(text);
  for (const tag of hashtags) {
    const normalizedTag = tag.toLowerCase();
    if (!isTopicalCandidate(normalizedTag, bannedWordsSet)) continue;
    
    // Check if it matches FOMO official communities
    const officialMatch = Object.keys(FOMO_COMMUNITIES).find(key => 
      key === normalizedTag || FOMO_COMMUNITIES[key].includes(normalizedTag)
    );
    
    if (officialMatch && !processedNames.has(officialMatch)) {
      communities.push({
        communityId: existingCommunities?.get(officialMatch)?.id,
        communityName: officialMatch,
        confidence: 0.95,
        source: 'hashtag',
        isOfficial: true
      });
      processedNames.add(officialMatch);
    } else if (!processedNames.has(normalizedTag)) {
      // Auto-generate community from hashtag
      communities.push({
        communityId: existingCommunities?.get(normalizedTag)?.id,
        communityName: normalizedTag,
        confidence: 0.9,
        source: 'hashtag',
        isOfficial: false
      });
      processedNames.add(normalizedTag);
    }
  }

  // 2. Extract semantic topics and map to communities (use text without mentions)
  const semanticTopics = inferSemanticTopics(textWithoutMentions, 10);
  for (const topic of semanticTopics) {
    const normalizedTopic = topic.topic.toLowerCase();
    if (!isTopicalCandidate(normalizedTopic, bannedWordsSet)) continue;
    
    // Check if topic matches FOMO communities
    for (const [communityName, keywords] of Object.entries(FOMO_COMMUNITIES)) {
      if (keywords.includes(normalizedTopic) && !processedNames.has(communityName)) {
        communities.push({
          communityId: existingCommunities?.get(communityName)?.id,
          communityName,
          confidence: topic.confidence * 0.8, // Slightly lower confidence for semantic
          source: 'semantic',
          isOfficial: true
        });
        processedNames.add(communityName);
        break;
      }
    }
    
    // If no official match and confidence is high enough, create auto-generated community
    if (!processedNames.has(normalizedTopic) && topic.confidence > 0.6) {
      const shouldAutoGenerate = semanticTopics.filter(t => 
        t.topic.toLowerCase() === normalizedTopic
      ).length > 0;
      
      if (shouldAutoGenerate) {
        communities.push({
          communityId: existingCommunities?.get(normalizedTopic)?.id,
          communityName: normalizedTopic,
          confidence: topic.confidence * 0.7,
          source: 'semantic',
          isOfficial: false
        });
        processedNames.add(normalizedTopic);
      }
    }
  }

  return communities;
}

/**
 * Extract communities from Google Vision API labels
 */
export function extractCommunitiesFromVisionLabels(
  labels: string[],
  existingCommunities?: Map<string, { id: string; isOfficial: boolean }>
): ExtractedCommunity[] {
  const communities: ExtractedCommunity[] = [];
  const processedNames = new Set<string>();

  for (const label of labels) {
    const normalizedLabel = label.toLowerCase();
    
    // Check each FOMO community's vision label mappings
    for (const [communityName, visionKeywords] of Object.entries(VISION_LABEL_MAPPING)) {
      if (visionKeywords.some(keyword => normalizedLabel.includes(keyword)) && 
          !processedNames.has(communityName)) {
        communities.push({
          communityId: existingCommunities?.get(communityName)?.id,
          communityName,
          confidence: 0.85,
          source: 'vision',
          isOfficial: true
        });
        processedNames.add(communityName);
      }
    }
  }

  return communities;
}

/**
 * Main function to extract communities from a post
 */
export async function extractCommunitiesFromPost(
  content: string,
  visionLabels?: string[],
  imageUrl?: string
): Promise<CommunityExtractionResult> {
  console.log('[extractCommunitiesFromPost] Content:', content?.substring(0, 100));
  console.log('[extractCommunitiesFromPost] Has vision labels:', !!visionLabels?.length);
  
  // Fetch existing communities for mapping
  const { data: existingCommunitiesData } = await supabase
    .from('communities')
    .select('id, name, is_fomo_official');
  
  const existingCommunities = new Map<string, { id: string; isOfficial: boolean }>();
  if (existingCommunitiesData) {
    for (const community of existingCommunitiesData) {
      existingCommunities.set(community.name, {
        id: community.id,
        isOfficial: community.is_fomo_official
      });
    }
  }

  // Extract communities from text
  console.log('[extractCommunitiesFromPost] Extracting from text...');
  const textCommunities = await extractCommunitiesFromText(content, existingCommunities);
  console.log('[extractCommunitiesFromPost] Text communities found:', textCommunities.length);
  
  // Extract communities from vision labels if provided
  const visionCommunities = visionLabels 
    ? extractCommunitiesFromVisionLabels(visionLabels, existingCommunities)
    : [];
  console.log('[extractCommunitiesFromPost] Vision communities found:', visionCommunities.length);

  // Merge and deduplicate communities
  const allCommunities = [...textCommunities, ...visionCommunities];
  const mergedCommunities = new Map<string, ExtractedCommunity>();
  
  for (const community of allCommunities) {
    const existing = mergedCommunities.get(community.communityName);
    if (!existing || community.confidence > existing.confidence) {
      mergedCommunities.set(community.communityName, community);
    }
  }

  // Sort by confidence and limit to top communities
  const finalCommunities = Array.from(mergedCommunities.values())
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 5); // Limit to 5 communities per post

  // Extract all topics for contextboard compatibility
  const topics = [
    ...extractHashtags(content),
    ...inferSemanticTopics(content).map(t => t.topic)
  ];

  return {
    communities: finalCommunities,
    topics: Array.from(new Set(topics)),
    visionLabels
  };
}

/**
 * Create or fetch community records in database
 */
export async function ensureCommunitiesExist(
  communities: ExtractedCommunity[]
): Promise<Map<string, string>> {
  const communityIdMap = new Map<string, string>();
  
  // CRITICAL: Load banned words as final safeguard before database creation
  const [prohibitedWords, softBlockWords] = await Promise.all([
    getProhibitedWords(),
    getSoftBlockWords()
  ]);
  
  const bannedWordsSet = new Set<string>();
  prohibitedWords.forEach(w => bannedWordsSet.add(w.word.toLowerCase()));
  softBlockWords.forEach(w => bannedWordsSet.add(w.word.toLowerCase()));
  
  for (const community of communities) {
    // CRITICAL: Final check - reject banned words before any database operations
    if (bannedWordsSet.has(community.communityName.toLowerCase())) {
      console.error(`[Community Security] BLOCKED attempt to create banned community: "${community.communityName}"`);
      continue; // Skip this community entirely
    }
    
    if (community.communityId) {
      communityIdMap.set(community.communityName, community.communityId);
      continue;
    }

    // Check if community exists (and fetch styling fields)
    const { data: existing } = await supabase
      .from('communities')
      .select('id, color, gradient')
      .eq('name', community.communityName)
      .maybeSingle();

    if (existing?.id) {
      // Backfill missing visual styling to guarantee gradients are always present
      const needsGradient = !existing.gradient || (existing.gradient as any)?.toString().trim() === '';
      const needsColor = !existing.color || (existing.color as any)?.toString().trim() === '';
      if (needsGradient || needsColor) {
        await supabase
          .from('communities')
          .update({
            gradient: needsGradient ? generateRandomGradient(community.communityName) : existing.gradient,
            color: needsColor ? generateColorFromString(community.communityName) : existing.color,
          })
          .eq('id', existing.id);
      }
      communityIdMap.set(community.communityName, existing.id);
    } else if (!community.isOfficial) {
      // Create auto-generated community
      const { data: newCommunity } = await supabase
        .from('communities')
        .insert({
          name: community.communityName,
          display_name: community.communityName.charAt(0).toUpperCase() + community.communityName.slice(1),
          description: `Community for ${community.communityName}`,
          icon: '✨', // Default icon for auto-generated
          color: generateColorFromString(community.communityName),
          gradient: generateRandomGradient(community.communityName),
          is_fomo_official: false,
          is_auto_generated: true
        })
        .select('id')
        .single();

      if (newCommunity) {
        communityIdMap.set(community.communityName, newCommunity.id);
      }
    }
  }

  return communityIdMap;
}

/**
 * Generate a consistent color from a string
 */
function generateColorFromString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 60%)`;
}

/**
 * Link communities to a loop post
 */
export async function linkCommunitiesToLoop(
  loopId: string,
  communities: ExtractedCommunity[],
  communityIdMap: Map<string, string>,
  disabledCommunityIds?: Set<string>,
  visiblePredictionKeys?: Set<string>
): Promise<void> {
  console.log(`[Community Link DEBUG] Input:`, {
    loopId,
    communitiesCount: communities.length,
    communityNames: communities.map(c => c.communityName),
    disabledCount: disabledCommunityIds?.size || 0,
    disabledIds: disabledCommunityIds ? Array.from(disabledCommunityIds) : [],
    visiblePredictionKeysCount: visiblePredictionKeys?.size || 0,
    visiblePredictionKeysArray: visiblePredictionKeys ? Array.from(visiblePredictionKeys) : [],
    communityIdMapEntries: Array.from(communityIdMap.entries())
  });

  const links = communities
    .filter(c => {
      // Check if community exists in map
      if (!communityIdMap.has(c.communityName)) {
        console.warn(`[Community Link] Community not in map: ${c.communityName}`);
        return false;
      }
      
      // BUG FIX: Check if community is disabled by BOTH ID and name
      // UI uses preview IDs (preview-{name}) but after creation we have real UUIDs
      const communityId = communityIdMap.get(c.communityName)!;
      if (disabledCommunityIds) {
        // Check both the real ID and the preview ID format
        const previewId = `preview-${c.communityName}`;
        const locId = `loc-${c.communityName}`;
        
        const checkResults = {
          hasRealId: disabledCommunityIds.has(communityId),
          hasPreviewId: disabledCommunityIds.has(previewId),
          hasLocId: disabledCommunityIds.has(locId),
          hasRawName: disabledCommunityIds.has(c.communityName)
        };
        
        const isDisabled = checkResults.hasRealId || checkResults.hasPreviewId || checkResults.hasLocId || checkResults.hasRawName;
        
        console.log(`[Community Link] Checking community: ${c.communityName}`, {
          communityId,
          previewId,
          locId,
          checkResults,
          isDisabled,
          allDisabledIds: Array.from(disabledCommunityIds)
        });
        
        if (isDisabled) {
          console.log(`[Community Link] ✅ Skipping disabled community: ${c.communityName} (${communityId})`);
          return false;
        }
      }

      // If an allowlist of visible predictions is provided, only link those
      if (visiblePredictionKeys && visiblePredictionKeys.size > 0) {
        const previewId = `preview-${c.communityName}`;
        const locId = `loc-${c.communityName}`;
        const allowed = visiblePredictionKeys.has(communityId) ||
                        visiblePredictionKeys.has(previewId) ||
                        visiblePredictionKeys.has(locId) ||
                        visiblePredictionKeys.has(c.communityName);
        if (!allowed) {
          console.log(`[Community Link] Skipping non-visible community: ${c.communityName} (${communityId})`);
          return false;
        }
      }
      
      console.log(`[Community Link] Allowing community: ${c.communityName} (${communityId})`);
      return true;
    })
    .map(c => ({
      loop_id: loopId,
      community_id: communityIdMap.get(c.communityName)!,
      confidence: c.confidence,
      source: c.source
    }));

  if (links.length > 0) {
    console.log(`[Community Link] Linking ${links.length} enabled communities to loop ${loopId}:`, links);
    try {
      const { error } = await supabase
        .from('loop_communities')
        .insert(links);
      
      if (error) {
        console.error(`[Community Link ERROR] Failed to insert:`, {
          error: error.message,
          code: error.code,
          details: error.details,
          links
        });
        throw error;
      }
      
      console.log(`[Community Link SUCCESS] Inserted ${links.length} community links`);
    } catch (error) {
      console.error(`[Community Link EXCEPTION]`, error);
      throw error;
    }
  } else {
    console.log(`[Community Link] No communities to link (all disabled or none extracted)`);
  }
}

/**
 * Track user interaction with a community
 */
export async function trackCommunityInteraction(
  userId: string,
  communityId: string,
  interactionType: 'view' | 'like' | 'comment' | 'post',
  weight: number = 1.0
): Promise<void> {
  await supabase
    .from('user_community_interactions')
    .insert({
      user_id: userId,
      community_id: communityId,
      interaction_type: interactionType,
      weight
    });
}
